import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Stack,
  Typography,
  CircularProgress,
  TextField as MuiTextField,
} from '@mui/material'

import LoginForm from '@hapana/shared/web/components/shared/auth/login-form/login-form.component'
import {
  EMAIL_FIELD_NAME,
  PASSWORD_FIELD_NAME,
} from '@hapana/shared/web/components/shared/auth/login-form/login-form.constants'
import HapanaLogo from '@hapana/shared/web/styles/assets/images/hapana-logo-green-full.png'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import ForgotPassword from '_pages/login/components/forgot-password/forgot-password.component.jsx'
import SendLoginLink from '_pages/login/components/send-login-link/send-login-link.component.jsx'
import { styles } from '_pages/login/login.styles'
import { extractSubdomain } from '../../services/tenant.service'
import { writeBrandHint } from '_utils/brand-hint.util'
import { buildRedirectSearch, sanitizeRedirectPath } from '_utils/redirect-path.util'
import useAuthStore from '../../stores/auth.store'
import {
  AuthFailureReason,
  readAuthFailureFromSearch,
} from '@hapana/shared/web/utils/auth-failure.utils'
import { useDemoMagicLink, useTrialMagicLink } from '../demo/demo.hooks'
import { getBareCoreDomain } from '../../utils/bare-core-domain.util'

const isDemoSubdomain = () => {
  const hostname = window.location.hostname
  if (hostname.split('.')[0] === 'demo') {
    return true
  }
  if (
    (hostname === 'localhost' || hostname === '127.0.0.1') &&
    import.meta.env.VITE_DEMO_STAFF_TENANT_ID
  ) {
    return true
  }
  return false
}

const DemoLoginForm = () => {
  const navigate = useNavigate()
  const { sendMagicLink, loading, error, sent } = useDemoMagicLink()
  const [email, setEmail] = useState(() => {
    try {
      const saved = localStorage.getItem('hapana_demo_signup')
      if (saved) {
        return JSON.parse(saved).email || ''
      }
    } catch {
      /* deliberately ignored: the caller proceeds regardless */
    }
    return ''
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    localStorage.setItem('demoEmailForSignIn', email)
    await sendMagicLink(email)
  }

  const content = (
    <Box sx={styles.root}>
      <Box sx={styles.flexCenter}>
        <Box component="img" alt="Hapana" src={HapanaLogo} sx={styles.logo} />
      </Box>
      <Box sx={styles.formRoot}>
        <Stack sx={styles.credentialsContainer}>
          <Stack>
            <Typography typography="header1" sx={styles.title}>
              {sent ? 'Check your email' : 'Log in to your demo'}
            </Typography>
            <Typography variant="body2" sx={{ marginTop: 1, color: 'gray.64' }}>
              {sent
                ? `We've sent a login link to ${email}. Click it to access your demo.`
                : "Enter your email and we'll send you a login link. No password needed."}
            </Typography>
          </Stack>
          {error && (
            <Box
              sx={{
                backgroundColor: '#FEE',
                border: '1px solid #FCC',
                borderRadius: 1,
                padding: 1.5,
                color: '#D8000C',
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
          {sent ? (
            <Stack spacing={1.5} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                Didn&apos;t receive it?{' '}
                <Box
                  component="span"
                  sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => sendMagicLink(email)}
                >
                  {loading ? 'Sending...' : 'Resend'}
                </Box>
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.85rem' }}>
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  Don&apos;t have a demo?{' '}
                </Box>
                <Box
                  component="span"
                  sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => navigate('/signup')}
                >
                  Sign up
                </Box>
              </Typography>
            </Stack>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                <MuiTextField
                  name="demoEmail"
                  label="Email"
                  placeholder="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                />
                <Button
                  label={loading ? <CircularProgress size={24} /> : 'Send me a login link'}
                  sx={styles.button}
                  disabled={loading || !email}
                  size="large"
                  type="submit"
                />
                <Stack sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    New to Hapana?{' '}
                    <Typography
                      component="span"
                      sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500 }}
                      onClick={() => navigate('/signup')}
                    >
                      Sign up for a demo
                    </Typography>
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
      <Box sx={styles.backgroundPatternImage} />
    </Box>
  )

  return <Box sx={styles.form}>{content}</Box>
}

const useTrialSubdomainCheck = () => {
  const [isTrial, setIsTrial] = useState(false)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    const hostname = window.location.hostname
    const subdomain = hostname.split('.')[0]

    // Skip trial check for demo, admin, localhost without tenant
    if (
      subdomain === 'demo' ||
      subdomain === 'admin' ||
      subdomain === 'localhost' ||
      subdomain === '127'
    ) {
      setIsChecking(false)
      return
    }

    const gatewayUrl = import.meta.env.VITE_GATEWAY_URL || ''
    fetch(`${gatewayUrl}/api/v1/auth/subdomain-info`)
      .then((res) => res.json())
      .then((data) => {
        setIsTrial(data.isTrial === true)
      })
      .catch(() => {
        // Silently fail — show standard login
      })
      .finally(() => {
        setIsChecking(false)
      })
  }, [])

  return { isTrial, isChecking }
}

const TrialLoginForm = () => {
  const { sendMagicLink, loading, error, sent } = useTrialMagicLink()
  const [email, setEmail] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    localStorage.setItem('trialEmailForSignIn', email)
    await sendMagicLink(email)
  }

  const content = (
    <Box sx={styles.root}>
      <Box sx={styles.flexCenter}>
        <Box component="img" alt="Hapana" src={HapanaLogo} sx={styles.logo} />
      </Box>
      <Box sx={styles.formRoot}>
        <Stack sx={styles.credentialsContainer}>
          <Stack>
            <Typography typography="header1" sx={styles.title}>
              {sent ? 'Check your email' : 'Log in'}
            </Typography>
            <Typography variant="body2" sx={{ marginTop: 1, color: 'gray.64' }}>
              {sent
                ? `We've sent a login link to ${email}. Click it to access your account.`
                : "Enter your email and we'll send you a login link. No password needed."}
            </Typography>
          </Stack>
          {error && (
            <Box
              sx={{
                backgroundColor: '#FEE',
                border: '1px solid #FCC',
                borderRadius: 1,
                padding: 1.5,
                color: '#D8000C',
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
          {sent ? (
            <Stack spacing={1.5} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                Didn&apos;t receive it?{' '}
                <Box
                  component="span"
                  sx={{ color: 'primary.main', cursor: 'pointer', fontWeight: 500 }}
                  onClick={() => sendMagicLink(email)}
                >
                  {loading ? 'Sending...' : 'Resend'}
                </Box>
              </Typography>
            </Stack>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                <MuiTextField
                  name="trialEmail"
                  label="Email"
                  placeholder="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                />
                <Button
                  label={loading ? <CircularProgress size={24} /> : 'Send me a login link'}
                  sx={styles.button}
                  disabled={loading || !email}
                  size="large"
                  type="submit"
                />
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
      <Box sx={styles.backgroundPatternImage} />
    </Box>
  )

  return <Box sx={styles.form}>{content}</Box>
}

const Login = () => {
  const { user } = useAuthStore()
  const { isTrial, isChecking } = useTrialSubdomainCheck()

  // Demo subdomain: if already authenticated, redirect to dashboard
  // Otherwise show magic link login instead of email/password
  if (isDemoSubdomain()) {
    if (user) {
      return <Navigate to="/" replace />
    }
    return <DemoLoginForm />
  }

  // Trial subdomain: show magic link login
  if (isChecking) {
    return null // Prevent flash while checking
  }

  if (isTrial) {
    if (user) {
      return <Navigate to="/" replace />
    }
    return <TrialLoginForm />
  }

  return <StandardLogin />
}

const StandardLogin = () => {
  const { pushDialog } = useDialog()
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const navigate = useNavigate()
  const location = useLocation()
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const isMountedRef = useRef(true)

  const { user, tenantError, loginError, signIn, clearLoginError } = useAuthStore()

  const wasInactivityLogout = location.state?.inactivityLogout === true
  const authFailure = readAuthFailureFromSearch(location.search)
  // Must be declared here (before any early returns) to satisfy Rules of Hooks
  const [emailValue, setEmailValue] = useState('')

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // OC-1294: remember which brand this browser signs in to, so the public articles.<root>
  // redirector can send a KB link to the right subdomain. The slug is already public in the
  // URL — this is a routing hint, never a credential, and it deliberately outlives the session.
  useEffect(() => {
    if (!user) {
      return
    }

    const subdomain = extractSubdomain()
    if (subdomain) {
      writeBrandHint(subdomain)
    }
  }, [user])

  const getHumanReadableError = (errorMessage) => {
    if (!errorMessage) {
      return t('errors:auth.generic')
    }

    if (
      errorMessage.includes('INVALID_LOGIN_CREDENTIALS') ||
      errorMessage.includes('auth/invalid-credential') ||
      errorMessage.includes('auth/user-not-found') ||
      errorMessage.includes('auth/wrong-password') ||
      errorMessage.includes('auth/invalid-email') ||
      errorMessage.includes('EMAIL_NOT_FOUND')
    ) {
      return t('errors:auth.loginFailed')
    }

    if (errorMessage.includes('auth/user-disabled')) {
      return t('errors:auth.accountDisabled')
    }

    if (errorMessage.includes('auth/too-many-requests')) {
      return t('errors:auth.tooManyRequests')
    }

    if (errorMessage.includes('auth/network-request-failed')) {
      return t('errors:auth.networkError')
    }

    return t('errors:auth.generic')
  }

  const displayError = loginError ? getHumanReadableError(loginError) : error

  const handleClearError = () => {
    setError('')
    clearLoginError()
  }

  if (tenantError && (tenantError.includes('not found') || tenantError.includes('404'))) {
    return (
      <Navigate
        to="/not-found"
        replace
        state={{
          error: 'Corporate account not found',
          subdomain: window.location.hostname.split('.')[0],
        }}
      />
    )
  }

  // Only auto-redirect if not mid-submission — during sign-in isLoading is true while
  // onIdTokenChanged sets user; let handleSubmit call navigate() instead so the form
  // stays visible with the loading spinner until navigation actually happens.
  if (user && !isLoading) {
    const urlParams = new URLSearchParams(location.search)
    const redirectParam = sanitizeRedirectPath(urlParams.get('redirect'))
    const fromLocation = location.state?.from
    const from = fromLocation
      ? `${fromLocation.pathname}${fromLocation.search || ''}`
      : redirectParam || '/'
    return <Navigate to={from} replace />
  }

  const handleSubmit = async (data) => {
    if (!isMountedRef.current) {
      return
    }

    handleClearError()
    setIsLoading(true)

    try {
      const result = await signIn(data[EMAIL_FIELD_NAME], data[PASSWORD_FIELD_NAME])

      if (!isMountedRef.current) {
        return
      }

      if (result && result.success) {
        // Check if this user has access to multiple brands (core-shared multi-brand setup).
        // If so, redirect to the brand picker before entering the app.
        try {
          const tokenResult = await result.user.getIdTokenResult(false)
          const sharedCorporateIds = tokenResult.claims.sharedCorporateIds
          if (Array.isArray(sharedCorporateIds) && sharedCorporateIds.length > 1) {
            const pendingRedirect = sanitizeRedirectPath(
              new URLSearchParams(location.search).get('redirect')
            )
            navigate(`/select-brand${buildRedirectSearch(pendingRedirect)}`, { replace: true })
            return
          }
        } catch (_) {
          // Non-critical — proceed with normal redirect if claims can't be read
        }

        const urlParams = new URLSearchParams(location.search)
        const redirectParam = sanitizeRedirectPath(urlParams.get('redirect'))
        const fromLocation = location.state?.from
        const from = fromLocation
          ? `${fromLocation.pathname}${fromLocation.search || ''}`
          : redirectParam || '/'
        navigate(from, { replace: true })
      } else {
        const errorMessage = getHumanReadableError(result?.error || 'Unknown error')

        if (isMountedRef.current) {
          setError(errorMessage)
        }
      }
    } catch (error) {
      const errorMessage = getHumanReadableError(error.message)

      if (isMountedRef.current) {
        setError(errorMessage)
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false)
      }
    }
  }

  // The bare domain has no tenant, so these requests would return 200 without sending an email.
  const showStudioUrlNoticeIfBareDomain = () => {
    const bareDomain = getBareCoreDomain(window.location.hostname)
    if (!bareDomain) {
      return false
    }
    openToast({
      title: t('login.bareDomain.title'),
      description: t('login.bareDomain.description', { example: `yourstudio.${bareDomain}` }),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.INFO,
    })
    return true
  }

  const handleForgotPassword = () => {
    handleClearError()
    if (showStudioUrlNoticeIfBareDomain()) {
      return
    }
    pushDialog(ForgotPassword, { defaultEmail: emailValue })
  }

  const handleSendLoginLink = () => {
    handleClearError()
    if (showStudioUrlNoticeIfBareDomain()) {
      return
    }
    pushDialog(SendLoginLink, { defaultEmail: emailValue })
  }

  return (
    <>
      {wasInactivityLogout && !authFailure && (
        <Alert severity="info" sx={styles.inactivityAlert}>
          {t(
            'settings.systemSettings.locationSettings.location-operations.autoLogout.sessionExpired'
          )}
        </Alert>
      )}
      {authFailure?.reason === AuthFailureReason.IpNotAllowed && (
        <Alert severity="warning" sx={styles.inactivityAlert} data-testid="login-ip-not-allowed">
          {authFailure.ipAddress
            ? t('login.authFailure.ipNotAllowedWithIp', { address: authFailure.ipAddress })
            : t('login.authFailure.ipNotAllowed')}
        </Alert>
      )}
      {authFailure?.reason === AuthFailureReason.SessionExpired && (
        <Alert severity="info" sx={styles.inactivityAlert} data-testid="login-session-expired">
          {t('login.authFailure.sessionExpired')}
        </Alert>
      )}
      <LoginForm
        onSubmit={handleSubmit}
        isLoading={isLoading}
        onForgotPassword={handleForgotPassword}
        onSendLoginLink={handleSendLoginLink}
        onEmailChange={setEmailValue}
        error={displayError}
        defaultValues={{
          [EMAIL_FIELD_NAME]: '',
          [PASSWORD_FIELD_NAME]: '',
        }}
        title={t('login.title')}
        description={t('login.description')}
        styles={styles}
      />
    </>
  )
}

export default Login
